/**
 * <s-bubble-film> — brings the soap-film bubbles to life.
 * - Pointer parallax: the window highlight, glint and inner reflection slide as the
 *   pointer moves over a bubble (sets --bubble-px / --bubble-py on it).
 * - Jiggle: bubbles wobble when the pointer enters and when a press is released.
 * Styles live in snippets/bubble-styles.liquid. Rendered once in layout/theme.liquid.
 */
const BUBBLES = '.glass-pill, .button-secondary, .button-surface, .button-outline, .button-primary, .glass-pill-dark, .ios-close, .theme-switch__knob, [aria-pressed="true"][class*="aria-pressed"][class*="bg-on-surface"]';
const MAX_SHIFT = 3;

class SBubbleFilm extends HTMLElement {
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  #frame = 0;
  #pending = null;

  #find(target) {
    if (!(target instanceof Element)) return null;
    const host = target.closest('[aria-pressed] > .theme-switch');
    if (host) return host.querySelector('.theme-switch__knob');
    const bubble = target.closest(BUBBLES);
    return bubble && !bubble.matches('[data-grid-view-group]') ? bubble : null;
  }

  #jiggle(bubble) {
    if (!bubble || bubble.matches(':disabled, [aria-disabled="true"], .is-popping')) return;
    bubble.classList.remove('is-jiggle');
    void bubble.offsetWidth;
    bubble.classList.add('is-jiggle');
  }

  #onOver = (event) => {
    if (this.#reduced.matches) return;
    const bubble = this.#find(event.target);
    if (!bubble || bubble === this.#find(event.relatedTarget)) return;
    this.#jiggle(bubble);
  };

  #onOut = (event) => {
    const bubble = this.#find(event.target);
    if (!bubble || bubble === this.#find(event.relatedTarget)) return;
    bubble.style.removeProperty('--bubble-px');
    bubble.style.removeProperty('--bubble-py');
  };

  #onMove = (event) => {
    if (this.#reduced.matches || event.pointerType === 'touch') return;
    this.#pending = event;
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const e = this.#pending;
      const bubble = e && this.#find(e.target);
      if (!bubble) return;
      const rect = bubble.getBoundingClientRect();
      const dx = Math.max(-1, Math.min(1, (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2 || 1)));
      const dy = Math.max(-1, Math.min(1, (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2 || 1)));
      bubble.style.setProperty('--bubble-px', `${(dx * MAX_SHIFT).toFixed(2)}px`);
      bubble.style.setProperty('--bubble-py', `${(dy * MAX_SHIFT).toFixed(2)}px`);
    });
  };

  #onDown = (event) => {
    const bubble = this.#find(event.target);
    if (bubble) bubble.classList.remove('is-jiggle');
  };

  #onUp = (event) => {
    if (this.#reduced.matches) return;
    this.#jiggle(this.#find(event.target));
  };

  #onEnd = (event) => {
    if (event.animationName === 'bubble-jiggle' && event.target instanceof Element) event.target.classList.remove('is-jiggle');
  };

  connectedCallback() {
    document.addEventListener('pointerover', this.#onOver, { passive: true });
    document.addEventListener('pointerout', this.#onOut, { passive: true });
    document.addEventListener('pointermove', this.#onMove, { passive: true });
    document.addEventListener('pointerdown', this.#onDown, { passive: true });
    document.addEventListener('pointerup', this.#onUp, { passive: true });
    document.addEventListener('animationend', this.#onEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerover', this.#onOver);
    document.removeEventListener('pointerout', this.#onOut);
    document.removeEventListener('pointermove', this.#onMove);
    document.removeEventListener('pointerdown', this.#onDown);
    document.removeEventListener('pointerup', this.#onUp);
    document.removeEventListener('animationend', this.#onEnd);
    cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('s-bubble-film')) customElements.define('s-bubble-film', SBubbleFilm);
